import { ProvisioningError } from "./link";
import type { DeviceIdentity, DeviceLink, ProvisioningStep } from "./link";
import type { DeviceEnrolment } from "@/types";

/**
 * K2, as a sequence.
 *
 * Flash, hand over the network, read the identity, enrol — four steps from one
 * button, each reported as it starts, finishes or fails. The person never
 * types a key or an id: the identity is read off the device and sent straight
 * to `enrolDevice` with the kit, and the label comes back made up.
 *
 * Pure apart from what it is given: a link (real or fake), a kit, and the
 * enrol call. That is what lets the unit tests run it end to end with no board
 * and no backend.
 */

export type StepState = "pending" | "running" | "done" | "failed";

export interface StepProgress {
  step: ProvisioningStep;
  state: StepState;
  /** Only while flashing: 0–100. */
  percent?: number;
}

export interface KitNetwork {
  id: string;
  ssid: string;
  psk: string;
}

export type EnrolCall = (input: {
  deviceId: string;
  publicKey: string;
  kitId: string;
}) => Promise<DeviceEnrolment>;

export interface ProvisioningResult {
  identity: DeviceIdentity;
  enrolment: DeviceEnrolment;
}

/** The sentence for a step that failed without saying why. */
const FALLBACK_KEY: Record<ProvisioningStep, string> = {
  flash: "devices.add.error.flash",
  network: "devices.add.error.network",
  identity: "devices.add.error.identity",
  enrol: "devices.add.error.enrol",
};

/**
 * Whatever a step threw, as a `ProvisioningError` for that step.
 *
 * A backend refusal already carries an i18n key (an `AppError`), and that key
 * is the right sentence — "that device is not registered" is more use than
 * "registering failed". Anything else gets the step's own sentence.
 */
function asProvisioningError(step: ProvisioningStep, error: unknown): ProvisioningError {
  if (error instanceof ProvisioningError) return error;

  const keyed = error as { key?: unknown; params?: unknown };
  if (typeof keyed?.key === "string") {
    const params =
      keyed.params && typeof keyed.params === "object"
        ? (keyed.params as Record<string, unknown>)
        : {};
    return new ProvisioningError(step, keyed.key, { cause: error, params });
  }
  return new ProvisioningError(step, FALLBACK_KEY[step], { cause: error });
}

export interface ProvisioningOptions {
  /**
   * Leave the firmware alone: it is already on this device.
   *
   * A device that took its software and then did not come back up is the most
   * ordinary failure there is — on a Core2 the board often has to be power
   * cycled before it will leave the flasher's arms — and the thing that
   * follows is not "write 1.4 MB again", it is "carry on from where it
   * stopped". Writing again is still offered; it is just no longer the only
   * thing on offer (2026-09-12, on the first real device).
   */
  skipFlash?: boolean;
}

export async function provisionDevice(
  link: DeviceLink,
  kit: KitNetwork,
  enrol: EnrolCall,
  report: (progress: StepProgress) => void,
  options: ProvisioningOptions = {}
): Promise<ProvisioningResult> {
  const run = async <T>(step: ProvisioningStep, work: () => Promise<T>): Promise<T> => {
    report({ step, state: "running" });
    try {
      const result = await work();
      report({ step, state: "done" });
      return result;
    } catch (error) {
      report({ step, state: "failed" });
      throw asProvisioningError(step, error);
    }
  };

  try {
    if (options.skipFlash) {
      // Said, not skipped silently: the list has to read like what happened.
      report({ step: "flash", state: "done" });
    } else {
      await run("flash", () =>
        link.flash((percent) => report({ step: "flash", state: "running", percent }))
      );
    }
    await run("network", () => link.sendNetwork(kit.ssid, kit.psk));
    const identity = await run("identity", () => link.readIdentity());
    const enrolment = await run("enrol", () =>
      enrol({ deviceId: identity.deviceId, publicKey: identity.publicKey, kitId: kit.id })
    );
    return { identity, enrolment };
  } finally {
    // The port is let go whether the device made it or not. A port held open
    // by a failed run is a device that cannot be tried again without
    // unplugging it, which is the first thing a person would try.
    await link.close();
  }
}
